import { useRef, useCallback, useMemo } from "react";
import { createSidebarTemplateSelectionSession } from "./sidebarTemplateSelectionSession";

/**
 * Stable ref-backed template selection session for sidebar dictation.
 */
export function useSidebarTemplateSelection(options) {
  const sessionRef = useRef(null);
  if (!sessionRef.current) {
    sessionRef.current = createSidebarTemplateSelectionSession(options);
  }

  const session = sessionRef.current;

  const resetForMicSession = useCallback(
    (options) => {
      session.resetForMicSession(options);
    },
    [session],
  );

  const onTranscriptCleared = useCallback(() => {
    session.onTranscriptCleared();
  }, [session]);

  const updateSidebarText = useCallback(
    (text) => session.updateSidebarText(text),
    [session],
  );

  const ingestRawCumulative = useCallback(
    (rawCumulative) => session.ingestRawCumulative(rawCumulative),
    [session],
  );

  const getVoiceTemplateGate = useCallback(
    (gateOptions) => session.getVoiceTemplateGate(gateOptions),
    [session],
  );

  const getRawCumulative = useCallback(() => session.rawCumulative, [session]);

  const getSessionText = useCallback(() => session.sessionText, [session]);

  const finalizeAfterTemplateAttempt = useCallback(
    () => session.finalizeAfterTemplateAttempt(),
    [session],
  );

  const lockAfterTemplateSelected = useCallback(
    () => session.lockAfterTemplateSelected(),
    [session],
  );

  return useMemo(
    () => ({
      resetForMicSession,
      onTranscriptCleared,
      updateSidebarText,
      ingestRawCumulative,
      getVoiceTemplateGate,
      getRawCumulative,
      getSessionText,
      finalizeAfterTemplateAttempt,
      lockAfterTemplateSelected,
    }),
    [
      resetForMicSession,
      onTranscriptCleared,
      updateSidebarText,
      ingestRawCumulative,
      getVoiceTemplateGate,
      getRawCumulative,
      getSessionText,
      finalizeAfterTemplateAttempt,
      lockAfterTemplateSelected,
    ],
  );
}
